import React from "react";
import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { ScatterChart } from "@mui/x-charts/ScatterChart";
import moment from "moment-timezone";
import AnalyticsHeader from "visual-components/analyticsDashboard/analytics-header";

/**
 * Ball legend indicator component
 */
function Ball({ color }) {
  return (
    <Box
      sx={{
        backgroundColor: color,
        display: "inline-block",
        width: 35,
        height: 35,
        borderRadius: "50%",
      }}
    />
  );
}

/**
 * Event Chart Component using MUI X Charts
 * Displays historical campaign performance timeline as a scatter chart
 * @param {Object} props
 * @param {Array} props.campaigns - Array of campaign data
 * @param {Date} props.earliestDate - Start date of timeline
 * @param {Date} props.latestDate - End date of timeline
 * @returns {import("react").ReactElement}
 */
export default function EventChart(props) {
  const { campaigns = [], earliestDate, latestDate } = props;

  if (!earliestDate || !latestDate) {
    return null;
  }

  const midpoint = new Date((earliestDate.getTime() + latestDate.getTime()) / 2);
  const formattedEarliest = moment(earliestDate).format("MMM YYYY");
  const formattedMidpoint = moment(midpoint).format("MMM YYYY");
  const formattedLatest = moment(latestDate).format("MMM YYYY");

  // Build scatter data for entries and NTF emails
  const entriesData = campaigns.map((campaign, index) => ({
    id: `entries-${campaign.id}`,
    x: index,
    y: 1,
    size: campaign.entries || 0,
    campaignName: campaign.name,
    period: `${moment(campaign.startDate).format("LL")} - ${moment(campaign.endDate).format("LL")}`,
  }));

  const ntfData = campaigns.map((campaign, index) => ({
    id: `ntf-${campaign.id}`,
    x: index,
    y: 0.5,
    size: campaign.newEmails || 0,
    campaignName: campaign.name,
    period: `${moment(campaign.startDate).format("LL")} - ${moment(campaign.endDate).format("LL")}`,
  }));

  // Find max for scaling
  const maxEntries = Math.max(...campaigns.map((c) => c.entries || 0), 1);

  return (
    <Card sx={{ width: "100%", overflow: "visible" }}>
      <CardHeader
        title={
          <AnalyticsHeader
            title="Historical Campaign Performance Timeline"
            tooltipText="Campaign performance over time showing entries and NTF emails"
          />
        }
      />
      <CardContent sx={{ p: 3, height: 300, position: "relative" }}>
        {/* Timeline line */}
        <Box
          sx={{
            position: "absolute",
            width: 868,
            height: 1,
            bgcolor: "#d8d8d8",
            top: 194,
            ml: 4,
          }}
        />

        {/* Date labels */}
        <Box sx={{ mx: 4, mt: 1, borderBottom: "1px solid #d8d8d8" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" fontWeight="bold">
              {formattedEarliest}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedMidpoint}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {formattedLatest}
            </Typography>
          </Box>
        </Box>

        {/* Chart title */}
        <Box sx={{ position: "absolute", ml: 4, mt: 3 }}>
          <Typography variant="subtitle2" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            Comparative Entries and NTF Driven by Campaign
          </Typography>
        </Box>

        {/* Scatter Chart */}
        <Box sx={{ height: 150, width: 900, display: "inline-block", mt: 4 }}>
          <ScatterChart
            series={[
              {
                data: entriesData,
                label: "Total Entries",
                color: "rgba(3, 169, 244, 0.5)",
                markerSize: 20,
                valueFormatter: (item) =>
                  `${item.campaignName}: ${item.size?.toLocaleString()} entries`,
              },
              {
                data: ntfData,
                label: "NTF Emails",
                color: "rgba(1, 118, 200, 0.5)",
                markerSize: 15,
                valueFormatter: (item) =>
                  `${item.campaignName}: ${item.size?.toLocaleString()} NTF emails`,
              },
            ]}
            xAxis={[
              {
                min: -0.5,
                max: campaigns.length - 0.5,
                tickLabelStyle: { display: "none" },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            yAxis={[
              {
                min: 0,
                max: 2,
                tickLabelStyle: { display: "none" },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            height={150}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            hideLegend
          />
        </Box>

        {/* Legend */}
        <Box sx={{ pl: 4, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Ball color="#b7c3cd" />
          <Typography variant="body2" sx={{ mr: 3 }}>
            = campaign
          </Typography>
          <Ball color="rgba(3, 169, 244, 0.5)" />
          <Typography variant="body2" sx={{ mr: 3 }}>
            = total entries
          </Typography>
          <Ball color="rgba(1, 118, 200, 0.5)" />
          <Typography variant="body2">= NTF emails</Typography>
        </Box>

        {/* Scale indicator */}
        <Box sx={{ pl: 4, mt: 1, display: "flex", alignItems: "center", gap: 2 }}>
          <Box>
            <svg width="35" height="8" viewBox="0 0 35 8" xmlns="http://www.w3.org/2000/svg">
              <line x1="1" y1="4" x2="34" y2="4" strokeWidth="2" stroke="black" />
              <line x1="1" y1="0" x2="1" y2="4" strokeWidth="2" stroke="black" />
              <line x1="34" y1="0" x2="34" y2="4" strokeWidth="2" stroke="black" />
              <line x1="18" y1="4" x2="18" y2="8" strokeWidth="2" stroke="black" />
            </svg>
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              scale of entries
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
